import { Box, Text, VStack, HStack, Badge, Icon, Spinner, Alert, AlertIcon, Image, Input, Button, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaCalendarAlt, FaFutbol, FaExclamationTriangle, FaMapMarkerAlt, FaCloud } from 'react-icons/fa';
import { useQuery } from '@tanstack/react-query';
import { api, API_URL } from '../../services/api';
import { useSettings } from '@/hooks/useSettings';
import { useTranslation } from 'react-i18next';
import { Widget } from './Widget';
import { format, parse, isToday, isTomorrow, isAfter } from 'date-fns';
import { cs } from 'date-fns/locale';
import { Match } from '../../types';
import { fetchTeamLogoOverrides } from '@/services/adminMatches';
import { assetUrl, sanitizeClubName } from '@/utils/url';
import { getCompetitionAliasesPublic, CompetitionAlias } from '@/services/competitionAliases';
import { TeamLogo } from '../common/TeamLogo';
import '../../styles/logos.css';

const FACR_DATE_FMT = 'dd.MM.yyyy HH:mm';

const formatMatchDate = (dateString: string) => {
  try {
    // Parse FACR date format from cache JSON (e.g., "17.08.2025 15:00")
    const date = parse(dateString, FACR_DATE_FMT, new Date());
    
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isTomorrow(date)) {
      return format(date, "HH:mm '(zítra)'");
    } else {
      return format(date, 'dd.MM. HH:mm');
    }
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return dateString;
  }
};

export const MatchesWidget: React.FC<{
  categoryName?: string;
  hideEmpty?: boolean;
  onMatchClick?: (match: Match) => void;
}> = ({ categoryName, hideEmpty = false, onMatchClick }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState<string>('');
  const [prefWeekly, setPrefWeekly] = useState<boolean>(true);
  const [prefMatches, setPrefMatches] = useState<boolean>(true);
  const [subscribing, setSubscribing] = useState<boolean>(false);
  const location = useLocation();
  const isAdminRoute = String(location.pathname || '').startsWith('/admin');

  const resolveUrl = (path: string) => {
    try {
      if (/^https?:\/\//i.test(path)) return path;
      const origin = new URL(API_URL, window.location.origin).origin;
      return origin + path;
    } catch {
      return path;
    }
  };

  const subscribe = async () => {
    if (!email) {
      toast({ title: t('form.email_required'), status: 'warning' });
      return;
    }
    setSubscribing(true);
    try {
      const res = await fetch(resolveUrl('/api/v1/newsletter/subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, preferences: { weekly: prefWeekly, matches: prefMatches } })
      });
      if (!res.ok) {
        const jt = await res.json().catch(() => ({} as any));
        throw new Error(jt?.error || `HTTP ${res.status}`);
      }
      toast({ title: t('matches.subscribe_success'), status: 'success' });
      setEmail('');
    } catch (e: any) {
      toast({ title: t('matches.subscribe_error'), description: e?.message || String(e), status: 'error' });
    } finally {
      setSubscribing(false);
    }
  };
  const { settings } = useSettings();
  
  // Check if a match is a home match
  const isHomeMatch = (match: Match) => {
    if (!match?.home || !settings) return false;
    const homeTeam = sanitizeClubName(match.home).toLowerCase();
    const clubName = sanitizeClubName((settings as any)?.club_name || '').toLowerCase();
    return homeTeam.includes(clubName) || clubName.includes(homeTeam);
  };

  const { data: overrides = {} } = useQuery({
    queryKey: ['teamLogoOverrides'],
    queryFn: fetchTeamLogoOverrides,
    staleTime: 5 * 60 * 1000,
  });
  const byId: Record<string, { name?: string; logo_url?: string }> = (overrides as any)?.by_id || {};
  const byNameMap: Record<string, string> = (overrides as any)?.by_name || {} as Record<string, string>;
  const normName = (s?: string) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-')
    .replace(/\bn\.?\b/g, ' nad ')
    .replace(/\bp\.?\b/g, ' pod ')
    .replace(/[\,\s]*(z\.?\s*s\.?|o\.?\s*s\.?)\s*$/g, '')
    .replace(/[\.,!;:()\[\]{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const aliasNameIndex = React.useMemo(() => {
    const urlToName: Record<string, string> = {};
    for (const v of Object.values(byId || {})) {
      const nm = String((v as any)?.name || '').trim();
      const lg = String((v as any)?.logo_url || '').trim();
      if (nm && lg) urlToName[lg] = nm;
    }
    const idx: Record<string, string> = {};
    for (const [alias, url] of Object.entries(byNameMap || {})) {
      const canon = urlToName[String(url)] || '';
      const key = normName(alias);
      if (canon && key) idx[key] = canon;
    }
    return idx;
  }, [byId, byNameMap]);
  const nameIndex = React.useMemo(() => {
    const idx: Record<string, { id: string; name: string; logo_url: string }> = {};
    try {
      for (const [id, v] of Object.entries(byId || {})) {
        const nm = String((v as any)?.name || '').trim();
        const lg = String((v as any)?.logo_url || '').trim();
        if (!nm) continue;
        const key = normName(nm);
        if (!key) continue;
        idx[key] = { id, name: nm, logo_url: lg };
      }
    } catch {}
    return idx;
  }, [byId]);
  const getOverrideName = (teamName?: string, teamId?: string) => {
    const tid = teamId ? String(teamId) : '';
    if (tid && byId?.[tid]?.name && String(byId[tid].name).trim()) {
      return String(byId[tid].name).trim();
    }
    try {
      const n = normName(teamName);
      if (aliasNameIndex[n]) return aliasNameIndex[n];
      let hit: any = nameIndex[n];
      if (!hit) {
        for (const [k, v] of Object.entries(nameIndex)) {
          if (!k) continue;
          if (n.endsWith(k) || k.endsWith(n)) { hit = v; break; }
        }
      }
      if (hit && (hit as any).name) return String((hit as any).name);
    } catch {}
    return String(teamName || '');
  };
  const getLogo = (teamName?: string, original?: string) => {
    const byName = (overrides as any)?.by_name || {} as Record<string, string>;
    const norm = (s: string) => String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const stripPrefixes = (s: string) => {
      let x = norm(s);
      x = x.replace(/\b(mestsky|m\.?f\.?k\.?|mfk|tj|sk|sokol|fotbalovy|fotbalový|fotbalovy\s+klub|fotbalovy\s+klub)\b/g, '');
      return x.replace(/\s+/g, ' ').trim();
    };
    const byNameNorm: Record<string, string> = Object.keys(byName || {}).reduce((acc: Record<string, string>, k) => { acc[norm(k)] = byName[k]; return acc; }, {});
    const strippedPairs = Object.keys(byName || {}).map((k) => ({ key: stripPrefixes(k), url: byName[k] }));
    const pick = (name?: string, orig?: string) => {
      if (!name) return orig;
      const exact = byName[name];
      let candidate = exact || byNameNorm[norm(name)];
      if (!candidate) {
        const s = stripPrefixes(name);
        for (const { key, url } of strippedPairs) { if (key && (s.endsWith(key) || key.endsWith(s))) { candidate = url; break; } }
      }
      const chosen = candidate || orig;
      if (typeof chosen === 'string' && chosen.startsWith('/')) {
        const origin = new URL(API_URL, window.location.origin).origin;
        return origin + chosen;
      }
      return chosen || (assetUrl('/dist/img/logo-club-empty.svg') as string);
    };
    return pick(teamName, original);
  };
  
  const { data: matches = [], isLoading, error } = useQuery<Match[]>({
    queryKey: ['upcomingMatchesCache'],
    queryFn: async () => {
      // Build absolute origin from API URL env (which may include /api/v1)
      const origin = new URL(API_URL, window.location.origin).origin;
      const url = `${origin}/cache/prefetch/facr_club_info.json`;

      const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
      if (!res.ok) throw new Error(`Failed to load cache: ${res.status}`);
      const json = await res.json();

      // Flatten competitions -> matches, enrich with competition name
      const comps = Array.isArray(json?.competitions) ? json.competitions : [];
      const items: any[] = comps.flatMap((c: any) =>
        (Array.isArray(c.matches) ? c.matches : []).map((m: any) => ({ ...m, competitionName: c.name, competition_id: c.id }))
      );

      // Parse, filter for future, sort ascending, take next 5
      const now = new Date();
      const upcoming = items
        .map((m) => ({
          ...m,
          __dt: parse(String(m.date_time || m.date), FACR_DATE_FMT, new Date()),
        }))
        .filter((m) => isAfter(m.__dt, now))
        .sort((a, b) => a.__dt.getTime() - b.__dt.getTime())
        .slice(0, 5)
        .map((m) => ({
          id: m.match_id,
          date_time: m.date_time || m.date,
          competitionName: m.competitionName,
          home: getOverrideName(m.home || m.home_team, m.home_id),
          away: getOverrideName(m.away || m.away_team, m.away_id),
          score: m.score,
          venue: m.venue,
          home_logo_url: (m.home_id && byId?.[m.home_id]?.logo_url) ? String(byId[m.home_id].logo_url) : getLogo(m.home || m.home_team, m.home_logo_url),
          away_logo_url: (m.away_id && byId?.[m.away_id]?.logo_url) ? String(byId[m.away_id].logo_url) : getLogo(m.away || m.away_team, m.away_logo_url),
          home_id: m.home_id,
          away_id: m.away_id,
        })) as Match[];

      return upcoming;
    },
  });

  // Load competition aliases (public) to resolve competition names to unified aliases like "U11"
  const aliasesQ = useQuery<{ list: CompetitionAlias[] }>({
    queryKey: ['competition-aliases-public'],
    queryFn: async () => {
      try {
        const list = await getCompetitionAliasesPublic();
        return { list };
      } catch {
        return { list: [] as CompetitionAlias[] };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const normalize = (s?: string) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const resolveAliasName = React.useCallback((compName?: string): string => {
    const name = String(compName || '');
    const normComp = normalize(name);
    const list = aliasesQ.data?.list || [];
    for (const a of list) {
      const aAlias = normalize(a.alias);
      const aOrig = normalize(a.original_name || '');
      if (aOrig && (normComp.includes(aOrig) || aOrig.includes(normComp))) return a.alias;
      if (aAlias && (normComp.includes(aAlias) || aAlias.includes(normComp))) return a.alias;
    }
    return name;
  }, [aliasesQ.data?.list]);

  const displayMatches = React.useMemo(() => {
    if (!Array.isArray(matches)) return [] as Match[];
    if (!categoryName) return matches as Match[];
    const needle = normalize(categoryName);
    const filtered = (matches as Match[]).filter((m: any) => {
      const comp = String((m as any).competitionName || '');
      const resolved = resolveAliasName(comp);
      const nComp = normalize(comp);
      const nResolved = normalize(resolved);
      // Prefer exact match on alias or competition name
      if (nResolved && nResolved === needle) return true;
      if (nComp === needle) return true;
      // Fallback: substring (kept for robustness when names vary)
      return (nResolved && nResolved.includes(needle)) || nComp.includes(needle);
    });
    // Respect hideEmpty: when no matches for the filter, return empty list
    return filtered;
  }, [matches, categoryName, resolveAliasName]);

  if (isLoading) {
    return (
      <Widget title={t('matches.upcoming')}>
        <VStack p={4}>
          <Spinner size="md" />
          <Text>{t('matches.loading')}</Text>
        </VStack>
      </Widget>
    );
  }

  if (error) {
    return (
      <Widget title={t('matches.upcoming')}>
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          {t('matches.error')}
        </Alert>
      </Widget>
    );
  }

  if (!displayMatches || displayMatches.length === 0) {
    if (hideEmpty) return null;
    return (
      <Widget title={t('matches.upcoming')}>
        <VStack p={4} spacing={4}>
          <Icon as={FaCalendarAlt} boxSize={8} color="gray.400" />
          <Text color="gray.500" textAlign="center">
            {t('matches.none_found')}
          </Text>
        </VStack>
      </Widget>
    );
  }

  return (
    <Widget title={t('matches.upcoming')}>
      <VStack spacing={{ base: 2, md: 3 }} align="stretch" divider={<Box borderBottomWidth="1px" borderColor="gray.200" />}>
        {displayMatches.map((match) => (
          <Box 
            key={match.id} 
            p={{ base: 3, md: 4 }} 
            bg="gray.50"
            _hover={{ bg: 'gray.100' }} 
            borderRadius="lg"
            transition="background-color 0.2s"
            shadow="sm"
            cursor={onMatchClick ? 'pointer' : 'default'}
            role={onMatchClick ? 'button' as any : undefined}
            tabIndex={onMatchClick ? 0 : undefined}
            onClick={() => onMatchClick && onMatchClick(match)}
            onKeyDown={(e) => {
              if (!onMatchClick) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onMatchClick(match);
              }
            }}
          >
            <HStack justify="space-between" mb={1} spacing={2} flexWrap="wrap">
              <Text 
                fontSize={{ base: 'xs', sm: 'sm' }} 
                color="gray.700" 
                fontWeight="medium"
                whiteSpace="nowrap"
              >
                {formatMatchDate(match.date_time)}
              </Text>
              <HStack spacing={1}>
                {isHomeMatch(match) && (
                  <Icon as={FaCloud} boxSize={3} color="blue.500" title="Domácí zápas - počasí v modálu" />
                )}
                <Badge 
                  colorScheme="blue" 
                  variant="subtle" 
                  fontSize="xs"
                  bg="blue.50"
                  color="blue.700"
                >
                  {match.competitionName}
                </Badge>
              </HStack>
            </HStack>
            
            <HStack justify="space-between" align="center">
              <HStack flex={1} minW={0} spacing={2}>
                <Box flexShrink={0} className="match-widget-logo">
                  <TeamLogo
                    teamId={(match as any).home_id}
                    teamName={match.home}
                    facrLogo={match.home_logo_url}
                    size="small"
                    fallbackIcon={<Icon as={FaFutbol} color="gray.400" boxSize={{ base: 4, md: 5 }} />}
                  />
                </Box>
                <Text 
                  fontSize={{ base: 'xs', sm: 'sm' }} 
                  fontWeight="medium" 
                  isTruncated
                  color="gray.800"
                >
                  {sanitizeClubName(match.home)}
                </Text>
              </HStack>
              <Text 
                fontSize={{ base: 'xs', sm: 'sm' }} 
                fontWeight="bold" 
                minW={{ base: '32px', sm: '40px' }} 
                textAlign="center"
                color="gray.900"
                flexShrink={0}
              >
                {match.score || 'vs'}
              </Text>
              <HStack flex={1} justify="flex-end" spacing={2} minW={0}>
                <Text 
                  fontSize={{ base: 'xs', sm: 'sm' }} 
                  fontWeight="medium" 
                  isTruncated 
                  textAlign="right"
                  color="gray.800"
                >
                  {sanitizeClubName(match.away)}
                </Text>
                <Box flexShrink={0} className="match-widget-logo">
                  <TeamLogo
                    teamId={(match as any).away_id}
                    teamName={match.away}
                    facrLogo={match.away_logo_url}
                    size="small"
                    fallbackIcon={<Icon as={FaFutbol} color="gray.400" boxSize={{ base: 4, md: 5 }} />}
                  />
                </Box>
              </HStack>
            </HStack>
            
            {match.venue && (
              <HStack mt={2} spacing={2} color="gray.500" fontSize="sm">
                <Icon as={FaMapMarkerAlt} boxSize={3} />
                <Text isTruncated>{match.venue}</Text>
              </HStack>
            )}
          </Box>
        ))}
        {/* Fan subscription form (hidden on admin pages) */}
        {!isAdminRoute && (
          <VStack p={3} spacing={2} align="stretch">
            <Text fontSize="sm" color="gray.600">{t('matches.subscribe_prompt')}</Text>
            <HStack>
              <Input type="email" placeholder={t('form.email_placeholder')} value={email} onChange={(e) => setEmail(e.target.value)} />
              <Button colorScheme="red" onClick={subscribe} isLoading={subscribing}>{t('matches.subscribe')}</Button>
            </HStack>
          </VStack>
        )}
      </VStack>
    </Widget>
  );
};
